import Papa from 'papaparse'
import { CRM_CONFIG } from '../config'
import type { CrmDataset } from '../types/crm'
import { createDataset } from './transform'

export type LoadCrmDatasetOptions = {
  cacheBust?: boolean
}

function exportUrl(gid: string, cacheBustToken?: string): string {
  const url = new URL(`https://docs.google.com/spreadsheets/d/${CRM_CONFIG.sheetId}/export`)
  url.searchParams.set('format', 'csv')
  url.searchParams.set('gid', gid)
  if (cacheBustToken) url.searchParams.set('_crm_refresh', cacheBustToken)
  return url.toString()
}

async function fetchSheetRows(
  gid: string,
  signal?: AbortSignal,
  cacheBustToken?: string,
): Promise<unknown[][]> {
  const response = await fetch(exportUrl(gid, cacheBustToken), {
    method: 'GET',
    cache: 'no-store',
    signal,
  })

  if (!response.ok) {
    throw new Error(`Google Sheets returned ${response.status}`)
  }

  const csv = await response.text()
  const parsed = Papa.parse<string[]>(csv, {
    skipEmptyLines: false,
  })

  if (parsed.errors.length > 0) {
    throw new Error(`Could not parse Google Sheet: ${parsed.errors[0].message}`)
  }

  return parsed.data
}

export async function loadCrmDataset(
  signal?: AbortSignal,
  options: LoadCrmDatasetOptions = {},
): Promise<CrmDataset> {
  const cacheBustToken = options.cacheBust ? String(Date.now()) : undefined
  const [organizationRows, opportunityRows] = await Promise.all([
    fetchSheetRows(CRM_CONFIG.sheets.organizations.gid, signal, cacheBustToken),
    fetchSheetRows(CRM_CONFIG.sheets.opportunities.gid, signal, cacheBustToken),
  ])

  return createDataset(organizationRows, opportunityRows)
}
